import {
  MaterialReactTable,
  MRT_ActionMenuItem,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BarLoader } from 'react-spinners';
import AddIcon from '@/assets/add.svg';
import DeleteIcon from '@/assets/disable.svg';
import ViewIcon from '@/assets/view.svg';
import { breadcrumbTitleKeys, useBreadcrumbsContext } from '@/breadcrumbs';
import {
  FilterItems,
  GroupAnalyticsFilters,
} from '@/components/filter/FilterItems';
import ConfirmAnnouncementModal from '@/components/modals/confirm-announcement';
import ConfirmResponseNotificationModal from '@/components/modals/confirm-response-notification';
import { Button } from '@/components/ui/button';
import { ButtonLink } from '@/components/ui/button-link';
import { Drawer } from '@/components/ui/drawer';
import { HeadingGroup } from '@/components/ui/heading-group';
import { Modal } from '@/components/ui/modal';
import { StatusBadge } from '@/components/ui/status-badge';
import { strings } from '@/constants/strings';
import { useCloseInvalidDrawer } from '@/hooks/use-close-invalid-drawer';
import { useCurrentOrganisation } from '@/hooks/use-current-organisation';
import { useGroup } from '@/hooks/use-group';
import { useGroupAnalytics } from '@/hooks/use-group-analytics';
import { useOpenState } from '@/hooks/use-open-state';
import { useUnitUrlComposer } from '@/hooks/use-unit-url-composer';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';
import { SendAnnouncementForm } from '@/pages/authenticated/devices/drawer-views/send-announcement-form';
import { api } from '@/redux/api';
import { useGetDevicesInOrganisationQuery } from '@/redux/api/endpoints/device';
import {
  composeTableOptions,
  getEntityRowId,
  getFilteredSelectedRows,
} from '@/table';
import { Unit } from '@/types/unit';
import { getOnlineStatus, useOnlineStatusMap } from '@/utils/get-online-status';
import { useAddNewUnitUrl } from './hooks/use-add-new-unit-url';
import { SelectedUnitsActionBar } from '../selected-units-action-bar';

type DrawerView = 'announce' | 'responseNotification';
type DrawerContext = { view: DrawerView; units: Unit[] };
type AnnouncementConfirmationContext = {
  units: Unit[];
  notificationText: string;
};
type ResponseNotificationConfirmationContext = {
  units: Unit[];
  notificationText: string;
};
type DeleteUnitConfirmationContext = {
  unit: Unit;
};

export const GroupPage: FC = () => {
  const navigate = useNavigate();
  const { setTitles: setBreadcrumbTitles } = useBreadcrumbsContext();
  const { groupId } = useParams<{ groupId: string }>();
  const parsedGroupId = parseInt(groupId ?? '-1');
  const { data: group } = useGroup(parsedGroupId);
  const { organisation: currentOrganisation } = useCurrentOrganisation();
  const groupOrganisation = useMemo(
    () =>
      group?.organisation
        ? { id: group.organisation.id, name: group.organisation.name }
        : currentOrganisation,
    [group?.organisation, currentOrganisation],
  );
  const { data: unitsData, isLoading: isLoadingUnits } = api.useGetUnitsQuery(
    { organisation: groupOrganisation! },
    { skip: !groupOrganisation },
  );
  const units = useMemo(
    () =>
      (unitsData ?? []).map((unit) => ({
        ...unit,
        organisation: groupOrganisation!,
      })),
    [unitsData, groupOrganisation],
  );
  const [deleteUnit] = api.useDeleteUnitMutation();
  const addUnitUrl = useAddNewUnitUrl();
  const composeUnitUrl = useUnitUrlComposer();
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const [filters, setFilters] = useState<GroupAnalyticsFilters>({
    dateRange: { start: undefined, end: undefined },
    missedCalls: 'all',
    callEndedBy: 'all',
    callDuration: 'all',
    callsMade: 'all',
    notificationsSent: 'all',
    announcementsSent: 'all',
    notificationResponses: 'all',
  });
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  const { data: devicesData, isFetching: isFetchingDevices } =
    useGetDevicesInOrganisationQuery(
      { organisation: groupOrganisation! },
      { skip: !groupOrganisation, pollingInterval: 30000 },
    );
  const devices = useMemo(
    () =>
      [...(devicesData ?? [])].sort((a, b) =>
        (a.meta?.serialNumber ?? '').localeCompare(b.meta?.serialNumber ?? ''),
      ),
    [devicesData],
  );

  /**
   * Map each unit ID to its online status based on associated device connectivity.
   * A unit is "Online" if at least one of its devices has an Online connectivity status.
   */
  const onlineStatusMap = useOnlineStatusMap(devices);

  const columns: MRT_ColumnDef<Unit>[] = useMemo(
    () => [
      {
        accessorKey: 'unitName',
        header: 'Unit Name',
        grow: true,
        size: 150,
        Cell: ({ renderedCellValue, row }) => {
          const composeUrl = useUnitUrlComposer();
          return (
            <Link to={composeUrl(row.original.id)}>{renderedCellValue}</Link>
          );
        },
      },
      {
        accessorKey: 'id',
        header: 'Unit #',
        size: 100,
      },
      {
        id: 'deviceCount',
        accessorFn: ({ devices }) => devices?.length || 0,
        filterVariant: 'range-slider',
        header: 'No. of Devices',
        size: 150,
        Cell: ({ renderedCellValue }) =>
          `${renderedCellValue} ${renderedCellValue === 1 ? 'device' : 'devices'}`,
      },
      {
        id: 'status',
        accessorFn: (row) => getOnlineStatus(onlineStatusMap, row.id),
        filterVariant: 'multi-select',
        header: 'Status',
        grow: false,
        size: 120,
        Cell: ({ row }) => {
          const status = getOnlineStatus(onlineStatusMap, row.original.id);
          if (!status) {
            return isFetchingDevices ? <BarLoader color={'gray'} /> : null;
          }
          return (
            <StatusBadge
              text={status}
              variant={status === 'Online' ? 'success' : 'alert'}
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onlineStatusMap, isFetchingDevices],
  );

  const groupName = group?.groupName ?? '';
  useEffect(() => {
    setBreadcrumbTitles((titles) => ({
      ...titles,
      [breadcrumbTitleKeys.currentGroupName]: groupName,
    }));
    return () => {
      setBreadcrumbTitles((titles) => ({
        ...titles,
        [breadcrumbTitleKeys.currentGroupName]: '',
      }));
    };
  }, [groupName, setBreadcrumbTitles]);

  /**
   * Drawer state for preparing an announcement
   */
  const {
    state: drawerState,
    open: openDrawer,
    toggleOpen: toggleDrawerOpen,
  } = useOpenState<DrawerContext>({ view: 'announce', units: [] });

  /**
   * Modal state for confirming the announcement
   */
  const {
    state: announcementConfirmationState,
    open: openAnnouncementConfirmation,
    toggleOpen: toggleAnnouncementConfirmationOpen,
  } = useOpenState<AnnouncementConfirmationContext>({
    units: [],
    notificationText: '',
  });

  /**
   * Response notification confirmation modal - check with the user before sending an
   * response notification
   */
  const {
    state: responseNotificationConfirmationState,
    open: openResponseNotificationConfirmation,
    toggleOpen: toggleResponseNotificationConfirmationOpen,
  } = useOpenState<ResponseNotificationConfirmationContext>({
    units: [],
    notificationText: '',
  });

  /**
   * Modal state for confirming unit deletion
   */
  const {
    state: deleteUnitConfirmationState,
    open: openDeleteUnitConfirmation,
    toggleOpen: toggleDeleteUnitConfirmationOpen,
  } = useOpenState<DeleteUnitConfirmationContext>({
    unit: {} as Unit,
  });

  /**
   * Our units query is for all units in the organisation, so needs to be filtered
   * to just this group
   */
  const filteredUnits = useMemo(
    () => units.filter((unit) => unit.group?.id === group?.id),
    [units, group],
  );

  const {
    filteredUnitIds,
    isLoading: isLoadingAnalytics,
    filtersActive,
    loadedCount,
    totalCount,
  } = useGroupAnalytics(filteredUnits, filters);

  /**
   * Apply analytics filtering when any filter is active
   */
  const displayUnits = useMemo(() => {
    if (!filtersActive) return filteredUnits;
    if (isLoadingAnalytics) return [];
    return filteredUnits.filter((unit) => filteredUnitIds.has(unit.id));
  }, [filteredUnits, filteredUnitIds, filtersActive, isLoadingAnalytics]);

  const table = useMaterialReactTable({
    ...composeTableOptions<Unit>({
      enableRowActions: true,
      enableRowSelection: true, // Enable row selection
    }),
    columns,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableStickyHeader: true,
    data: displayUnits,
    getRowId: getEntityRowId,
    state: {
      isLoading: isLoadingUnits || (filtersActive && isLoadingAnalytics),
      showColumnFilters: true,
      rowSelection, // Add row selection state
      ...(isLoadingUnits
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    onRowSelectionChange: setRowSelection, // Handle row selection
    renderRowActionMenuItems: ({ row, table, closeMenu }) => [
      <MRT_ActionMenuItem
        icon={<ViewIcon />}
        key="view"
        label="View"
        onClick={() => {
          closeMenu();
          navigate(composeUnitUrl(row.original.id));
        }}
        table={table}
      />,
      <MRT_ActionMenuItem
        icon={<DeleteIcon />}
        key="delete"
        label="Delete"
        onClick={() => {
          closeMenu();
          openDeleteUnitConfirmation({ unit: row.original });
        }}
        table={table}
      />,
    ],
  });

  const selectedUnits = getFilteredSelectedRows(table);
  const selectedUnitsHaveDevices = selectedUnits.some(
    (unit) => unit.devices && unit.devices.length > 0,
  );

  const getUnitIds = (units: Unit[]): number[] =>
    units.map((unit) => unit.id).filter(Boolean) as number[];

  const unassignedUnits = units.filter((unit) => !unit.group);

  useCloseInvalidDrawer(
    drawerState.open,
    Boolean(drawerState.context.units?.length),
    toggleDrawerOpen,
  );

  const renderDrawerContent = () => {
    switch (drawerState.context.view) {
      case 'announce': {
        const { units } = drawerState.context;
        if (units && units.length > 0) {
          return (
            <SendAnnouncementForm
              onCancel={() => toggleDrawerOpen(false)}
              onSubmit={(data) => {
                openAnnouncementConfirmation({
                  units,
                  notificationText: data.notificationText,
                });
              }}
            />
          );
        }
        break;
      }
      case 'responseNotification': {
        const { units } = drawerState.context;
        if (units && units.length > 0) {
          return (
            <SendAnnouncementForm
              onCancel={() => toggleDrawerOpen(false)}
              onSubmit={(data) => {
                openResponseNotificationConfirmation({
                  units: units,
                  notificationText: data.notificationText,
                });
              }}
            />
          );
        }
        break;
      }
      default:
        break;
    }

    return null;
  };

  if (!group) {
    // TODO: Loading
    return null;
  }

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitlePrefix={`Units in ${group?.groupName}`}
          title={strings.group.title}
        />
        <div className="flex items-center justify-end gap-2">
          {unassignedUnits.length > 0 && (
            <ButtonLink to="associate-units" variant="secondary">
              <AddIcon /> Link Existing Units
            </ButtonLink>
          )}
          <Button onClick={() => navigate(addUnitUrl)}>
            <AddIcon /> Add New Unit
          </Button>
        </div>
      </HeadingGroupContainerLayout>

      <SelectedUnitsActionBar
        hasUnitsWithDevices={selectedUnitsHaveDevices}
        openSendAnnouncement={() =>
          openDrawer({ view: 'announce', units: selectedUnits })
        }
        openSendResponseNotification={() =>
          openDrawer({ view: 'responseNotification', units: selectedUnits })
        }
        selectedUnits={selectedUnits}
      />

      <div className="w-full rounded-t-lg border border-gray-200 bg-white shadow-xs">
        <button
          className="flex w-full items-center justify-between p-4 text-left hover:bg-gray-50"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <h3 className="text-sm font-semibold">Unit Filters</h3>
          <span className="text-gray-500">
            {filtersExpanded ? '\u2212' : '+'}
          </span>
        </button>
        {filtersExpanded && (
          <div className="border-t border-gray-200 p-6">
            <FilterItems filters={filters} onFiltersChange={setFilters} />
          </div>
        )}
      </div>

      {filtersActive && isLoadingAnalytics && (
        <div className="px-4 py-2 text-sm text-gray-500">
          Loading analytics data... ({loadedCount}/{totalCount} units)
        </div>
      )}

      <MaterialReactTable table={table} />

      <Drawer
        open={drawerState.open}
        setOpen={toggleDrawerOpen}
        title="Notification"
      >
        {renderDrawerContent()}
      </Drawer>

      <ConfirmAnnouncementModal
        isOpen={announcementConfirmationState.open}
        notificationText={
          announcementConfirmationState.context.notificationText
        }
        onCancel={() => toggleAnnouncementConfirmationOpen(false)}
        onSend={() => {
          toggleAnnouncementConfirmationOpen(false);
          toggleDrawerOpen(false);
          setRowSelection({});
        }}
        unitIds={getUnitIds(announcementConfirmationState.context.units)}
      />
      <ConfirmResponseNotificationModal
        isOpen={responseNotificationConfirmationState.open}
        notificationText={
          responseNotificationConfirmationState.context.notificationText
        }
        onCancel={() => toggleResponseNotificationConfirmationOpen(false)}
        onSend={() => {
          toggleResponseNotificationConfirmationOpen(false);
          toggleDrawerOpen(false);
        }}
        unitIds={getUnitIds(
          responseNotificationConfirmationState.context.units,
        )}
      />

      {/* Delete unit confirmation modal */}
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleDeleteUnitConfirmationOpen(false)}
            variant="secondary"
          >
            Cancel
          </Button>,
          <Button
            disabled={
              deleteUnitConfirmationState.context.unit?.devices?.length > 0
            }
            key="delete"
            onClick={() => {
              const unit = deleteUnitConfirmationState.context?.unit;
              if (unit?.id && unit?.organisation) {
                deleteUnit({
                  id: unit.id,
                  organisation: unit.organisation,
                });
                toggleDeleteUnitConfirmationOpen(false);
              }
            }}
            variant="primary-warning"
          >
            Delete
          </Button>,
        ]}
        description={
          deleteUnitConfirmationState.context?.unit?.devices?.length > 0
            ? `You cannot delete a unit which has associated devices`
            : `Are you sure you want to delete unit ${deleteUnitConfirmationState.context.unit?.unitName || ''}? This action cannot be undone.`
        }
        open={deleteUnitConfirmationState.open}
        setOpen={toggleDeleteUnitConfirmationOpen}
        title="Delete Unit"
      />
    </>
  );
};
